import { API } from '../api';
import {
    ICreateStandbyPeriod,
    ICreateStandbyPeriodBatch,
    ICreateStandbyPeriodBatchByProgram,
    IFetchStandbyPeriodsInBatchRequestParams,
    IFetchStandbyPeriodsRequestParams,
    IStandbyPeriod,
} from './interface';
import { IFetchEventsRequestParams } from '../event/interface';
import { IPagination } from '../IPagination';

export const createStandbyPeriodBatch = async (standbyPeriod: ICreateStandbyPeriod, sites: number[]) => {
    const response = await API.fetch(`/standby-period/sites/batch`, {
        method: 'post',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ standby_period: standbyPeriod, site_ids: sites } as ICreateStandbyPeriodBatch),
    });

    if (response.ok) {
        return;
    }

    const respBody: any = await response.json();
    throw new Error(respBody?.error?.message || `Cannot save standby period!`);
};

export const createStandbyPeriodBatchByProgram = async (standbyPeriod: ICreateStandbyPeriod, program: number) => {
    const response = await API.fetch('/standby-period/programs/batch', {
        method: 'post',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ standby_period: standbyPeriod, program } as ICreateStandbyPeriodBatchByProgram),
    });

    if (response.ok) {
        return;
    }

    const respBody: any = await response.json();
    throw new Error(respBody?.error?.message || `Cannot save standby period!`);
};

export const updateStandbyPeriod = async (standbyPeriodId: number, standbyPeriod: Partial<IStandbyPeriod>) => {
    const response = await API.fetch(`/standby-period/${standbyPeriodId}`, {
        method: 'put',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(standbyPeriod),
    });
    const respBody: any = await response.json();

    if (response.ok) {
        return;
    }
    throw new Error(respBody?.error?.message || `Cannot update standby period!`);
};

export const updateStandbyPeriodBatch = async (batchUuid: string, standbyPeriod: any, companyId?: number) => {
    const query = `?${new URLSearchParams({ ...(companyId && { companyId: companyId + '' }) })}`;
    const response = await API.fetch(`/standby-period/batch/${batchUuid}${query}`, {
        method: 'put',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(standbyPeriod),
    });

    if (response.ok) {
        return;
    }

    const respBody: any = await response.json();
    throw new Error(respBody?.error?.message || `Cannot update batch!`);
};

export const cancelStandbyPeriod = async (standbyPeriodId: number) => {
    const response = await API.fetch(`/standby-period/${standbyPeriodId}`, {
        method: 'delete',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    if (response.ok) {
        return;
    }

    const respBody: any = await response.json();
    throw new Error(respBody?.error?.message || `Cannot cancel standby period!`);
};

export const cancelStandbyPeriodBatch = async (batchUuid: string, companyId?: number) => {
    const query = `?${new URLSearchParams({ ...(companyId && { companyId: companyId + '' }) })}`;
    const response = await API.fetch(`/standby-period/batch/${batchUuid}${query}`, {
        method: 'delete',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    if (response.ok) {
        return;
    }

    const respBody: any = await response.json();
    throw new Error(respBody?.error?.message || `Cannot cancel batch!`);
};

export const fetchStandbyPeriodsInBatch = async (
    { batchUuid, include, companyId }: IFetchStandbyPeriodsInBatchRequestParams,
    signal: AbortSignal | null = null
) => {
    const query = `?${new URLSearchParams({
        ...(include && { include }),
        ...(companyId && { companyId: companyId + '' }),
    })}`;
    const response = await API.fetch(`/standby-period/batch/${batchUuid}${query}`, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();
    if (response.ok) {
        return respBody;
    }

    throw new Error(response.statusText);
};

export const fetchStandbyPeriods = async (
    params: IFetchStandbyPeriodsRequestParams,
    signal: AbortSignal | null = null
): Promise<IPagination<IStandbyPeriod>> => {
    let { pagination = { pageSize: 10000, current: 1 }, include, sorter } = params;

    const limit = pagination.pageSize!;
    const offset = (pagination.current! - 1) * limit;

    if (sorter && (!sorter.field || !sorter.order)) {
        sorter = {};
    }

    const path = `/standby-period?${new URLSearchParams({
        ...prepareFilter(params),
        ...(include && { include }),
        ...(sorter?.field && { sortField: sorter.field, sortOrder: sorter.order }),
        offset: offset.toString(),
        limit: limit.toString(),
    })}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();
    if (response.ok) {
        return respBody;
    }
    throw new Error(response.statusText);
};

function prepareFilter(filter: IFetchStandbyPeriodsRequestParams) {
    updateMarketZoneFilter(filter);

    const { companyId = null, programIds = null, siteIds = null, siteUtilities = null, loadZone, market, start, end, search } = filter;

    return {
        ...(companyId && { companyId }),
        ...(programIds && { programIds }),
        ...(siteIds && { siteIds }),
        ...(siteUtilities && { siteUtilities }),
        ...(loadZone && { loadZone }),
        ...(market && { market }),
        ...(start && { start: start.toISOString() }),
        ...(end && { end: end.toISOString() }),
        ...(search && { search }),
    };
}

function updateMarketZoneFilter(filter: { load_zone?: string; market?: string | null; [key: string]: any }): IFetchEventsRequestParams {
    if (filter.load_zone && filter.load_zone.includes('null-')) {
        filter.load_zone = 'null';
    }

    if (filter.load_zone && filter.load_zone.includes('undefined-')) {
        filter.load_zone = undefined;
    }

    if (filter.market && filter.market.includes('null-')) {
        filter.market = 'null';
    }

    if (filter.market && filter.market.includes('undefined-')) {
        filter.market = undefined;
    }

    return filter;
}
